import React, { useState, useEffect } from "react";

import {
  Button,
  Dialog,
  makeStyles,
  Typography,
  IconButton,
  Box,
  CircularProgress,
} from "@material-ui/core";

import CloseIcon from "@material-ui/icons/Close";
import SettingsInputAntennaIcon from "@material-ui/icons/SettingsInputAntenna";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: "16px",
    width: "100%",
    maxWidth: 480,
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: "0 20px 50px rgba(0, 0, 0, 0.15)",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(0, 0, 0, 0.06)"
    }`,
  },
  dialogTitle: {
    padding: theme.spacing(2, 3),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "#f0f2f5"
    }`,
    flexShrink: 0,
  },
  headerIconWrapper: {
    width: 42,
    height: 42,
    borderRadius: "12px",
    background:
      "linear-gradient(135deg, rgba(13,148,136, 0.15) 0%, rgba(13,148,136, 0.04) 100%)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 14,
    color: "#0D9488",
    border: "1px solid rgba(13,148,136, 0.2)",
  },
  titleText: {
    fontWeight: 700,
    fontSize: "1.15rem",
    color: theme.palette.text.primary,
  },
  subtitleText: {
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
    marginTop: 1,
  },
  closeBtn: {
    color: theme.palette.text.secondary,
    padding: 8,
    borderRadius: "10px",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.05)",
      color: theme.palette.text.primary,
    },
  },
  dialogContent: {
    padding: theme.spacing(2.5, 3),
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    "&::-webkit-scrollbar": { width: "5px" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(13,148,136, 0.25)",
      borderRadius: "4px",
    },
  },
  aviso: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
    marginBottom: theme.spacing(1),
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 14px",
    borderRadius: "12px",
    border: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255,255,255,0.10)" : "#e2e8f0"
    }`,
    backgroundColor:
      theme.palette.type === "dark" ? "rgba(255,255,255,0.03)" : "#f8fafc",
    cursor: "pointer",
    transition: "all 0.15s ease",
    "&:hover": {
      borderColor: "#0D9488",
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(13,148,136,0.10)"
          : "rgba(13,148,136,0.06)",
    },
  },
  itemSelecionado: {
    borderColor: "#0D9488",
    borderWidth: 1.5,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(13,148,136,0.14)"
        : "rgba(13,148,136,0.08)",
  },
  itemNome: {
    flex: 1,
    minWidth: 0,
    fontSize: "0.92rem",
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  itemStatus: {
    fontSize: "0.72rem",
    color: theme.palette.text.secondary,
    display: "flex",
    alignItems: "center",
    gap: 5,
    flexShrink: 0,
  },
  etiquetaAtual: {
    fontSize: "0.66rem",
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#0D9488",
    backgroundColor: "rgba(13,148,136,0.12)",
    borderRadius: 6,
    padding: "2px 7px",
    flexShrink: 0,
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "#f0f2f5"
    }`,
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1.5),
    flexShrink: 0,
  },
  btnCancel: {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    padding: "8px 18px",
    color: theme.palette.text.secondary,
    border: `1px solid ${
      theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.12)" : "#d0d5dd"
    }`,
  },
  btnSave: {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
    padding: "8px 24px",
    backgroundColor: "#0D9488",
    color: "#ffffff",
    boxShadow: "0 2px 8px rgba(13,148,136, 0.25)",
    "&:hover": { backgroundColor: "#0f766e" },
  },
}));

// Cor da bolinha pelo estado da conexao. So o CONNECTED responde cliente;
// os outros aparecem assim mesmo, para o atendente saber o que esta pegando.
const corDoStatus = (status) => {
  if (status === "CONNECTED") return "#14b8a6";
  if (status === "qrcode" || status === "OPENING" || status === "PAIRING")
    return "#f59e0b";
  return "#ef4444";
};

const rotuloDoStatus = (status) => {
  if (status === "CONNECTED") return "Conectado";
  if (status === "qrcode") return "Aguardando QR Code";
  if (status === "OPENING") return "Conectando";
  if (status === "PAIRING") return "Pareando";
  if (status === "TIMEOUT") return "Sem resposta";
  return "Desconectado";
};

// Troca o numero pelo qual o atendimento continua. Serve tanto para o
// atendimento que ficou orfao (a conexao dele foi excluida) quanto para
// mudar de numero no meio da conversa.
const TrocarConexaoModal = ({ modalOpen, onClose, ticketId, whatsappIdAtual }) => {
  const classes = useStyles();

  const [conexoes, setConexoes] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [escolhida, setEscolhida] = useState(null);

  useEffect(() => {
    if (!modalOpen) return;
    setEscolhida(whatsappIdAtual || null);
    setCarregando(true);
    (async () => {
      try {
        const { data } = await api.get("/whatsapp");
        const lista = Array.isArray(data) ? data : [];
        // Conectadas primeiro: e a que o atendente quer na maioria das vezes.
        lista.sort(
          (a, b) =>
            (b.status === "CONNECTED") - (a.status === "CONNECTED") ||
            String(a.name || "").localeCompare(String(b.name || ""), "pt-BR")
        );
        setConexoes(lista);
      } catch (err) {
        toastError(err);
      } finally {
        setCarregando(false);
      }
    })();
  }, [modalOpen, whatsappIdAtual]);

  const fechar = () => {
    onClose();
    setEscolhida(null);
  };

  const salvar = async () => {
    if (!ticketId || !escolhida) return;
    setSalvando(true);
    try {
      await api.put(`/tickets/${ticketId}`, { whatsappId: escolhida });
      const nome = conexoes.find((c) => c.id === escolhida)?.name;
      toast.success(
        nome
          ? `Atendimento movido para a conexão ${nome}.`
          : "Conexão do atendimento alterada."
      );
      fechar();
    } catch (err) {
      toastError(err);
    } finally {
      setSalvando(false);
    }
  };

  const semConexao = !whatsappIdAtual;

  return (
    <Dialog
      open={modalOpen}
      onClose={fechar}
      maxWidth="xs"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <div className={classes.dialogTitle}>
        <Box display="flex" alignItems="center">
          <div className={classes.headerIconWrapper}>
            <SettingsInputAntennaIcon style={{ fontSize: 24 }} />
          </div>
          <div>
            <Typography className={classes.titleText}>Trocar conexão</Typography>
            <Typography className={classes.subtitleText}>
              Escolha por qual número este atendimento continua
            </Typography>
          </div>
        </Box>
        <IconButton className={classes.closeBtn} onClick={fechar}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <div className={classes.dialogContent}>
        <Typography className={classes.aviso}>
          {semConexao
            ? "Este atendimento está sem conexão (a anterior foi excluída). Escolha uma para voltar a responder o cliente."
            : "As próximas mensagens sairão pelo número escolhido. O cliente vai ver a conversa chegando de outro número."}
        </Typography>

        {carregando && (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={22} style={{ color: "#0D9488" }} />
          </Box>
        )}

        {!carregando && conexoes.length === 0 && (
          <Typography className={classes.aviso}>
            Nenhuma conexão cadastrada. Cadastre uma em Conexões.
          </Typography>
        )}

        {!carregando &&
          conexoes.map((c) => (
            <div
              key={c.id}
              className={`${classes.item} ${
                escolhida === c.id ? classes.itemSelecionado : ""
              }`}
              onClick={() => setEscolhida(c.id)}
            >
              <span
                style={{
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  backgroundColor: corDoStatus(c.status),
                  flexShrink: 0,
                }}
              />
              <span className={classes.itemNome}>{c.name}</span>
              {c.id === whatsappIdAtual && (
                <span className={classes.etiquetaAtual}>Atual</span>
              )}
              <span className={classes.itemStatus}>{rotuloDoStatus(c.status)}</span>
              {escolhida === c.id && (
                <CheckCircleIcon style={{ fontSize: 18, color: "#0D9488" }} />
              )}
            </div>
          ))}
      </div>

      <div className={classes.dialogActions}>
        <Button onClick={fechar} disabled={salvando} className={classes.btnCancel}>
          Cancelar
        </Button>
        <ButtonWithSpinner
          variant="contained"
          loading={salvando}
          disabled={salvando || !escolhida || escolhida === whatsappIdAtual}
          onClick={salvar}
          className={classes.btnSave}
        >
          Trocar conexão
        </ButtonWithSpinner>
      </div>
    </Dialog>
  );
};

export default TrocarConexaoModal;
